import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import {
  Box,
  Heading,
  Stack,
  Text,
  TextLink,
  useResponsiveValue
} from '@machobear/design-system'

import emailIcon from '@/features/Company/icons/email-icon.png'
import globeIcon from '@/features/Company/icons/globe-icon.png'
import phoneIcon from '@/features/Company/icons/phone-icon.png'
import Head from '@/components/head'

import heroBanner from './hero.jpg'
import titleLogo from './logo.jpg'

const FirstarRealtyIncProfile = ({ metadata }) => {
  const { t } = useTranslation('firstarrealtyinc')
  const profile = {
    banner: heroBanner.src,
    title: t('firstarrealtyinc:title'),
    pageDescription: t('firstarrealtyinc:description1'),
    url: '/company/firstarrealtyinc'
  }

  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false
  })

  const isDesktop = useResponsiveValue()({
    mobile: false,
    tablet: false,
    desktop: true
  })

  return (
    <>
      <Head
        title={`${profile.title} | liv.rent`}
        description={profile.pageDescription}
        path={profile.url}
        pageData={metadata}
        hideCustomerSupport
      />
      <Box
        style={{ height: isMobile ? '210px' : isDesktop ? '575px' : '350px' }}
        marginBottom="gutter"
        overflow="hidden"
        position="relative"
      >
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="full"
          width="full"
          overflow="hidden"
        >
          <Image
            fill
            priority
            src={profile.banner}
            style={{
              objectFit: 'cover',
              objectPosition: 'left'
            }}
            sizes="(max-width: 768px) 768px, (max-width: 1200px) 1200px"
            alt={t('firstarrealtyinc:title')}
          />
        </Box>
      </Box>
      <Box
        display="flex"
        justifyContent="center"
        width="full"
        marginBottom="large"
      >
        <Stack space="medium" alignItems="center">
          {' '}
          <Box display="flex" alignItems="center" marginRight="large">
            <Box
              marginRight="xsmall"
              marginLeft="medium"
              style={{ flexShrink: 0 }}
            >
              <Image
                src={titleLogo.src}
                width={75}
                height={75}
                alt={t('firstarrealtyinc:title')}
              />
            </Box>
            <Heading level="3" weight="weak">
              {t('firstarrealtyinc:heading')}
            </Heading>
          </Box>
          <Box
            display="flex"
            flexDirection={{ mobile: 'column', tablet: 'row' }}
            gap={{ mobile: 'gutter', tablet: 'xlarge' }}
            marginX="medium"
            marginBottom="xlarge"
            marginTop="medium"
            style={{
              maxWidth: '1096px'
            }}
            width="full"
            justifyContent="spaceBetween"
          >
            <Box
              display="flex"
              flexDirection="column"
              flex={2}
              marginRight={{ mobile: 'none', tablet: 'xxlarge' }}
            >
              <Stack space="medium">
                <Stack space="gutter">
                  <Text size="standard" weight="strong">
                    {' '}
                    {t('firstarrealtyinc:subheading1')}
                  </Text>
                  <Text size="standard">
                    {t('firstarrealtyinc:subheading2')}
                  </Text>
                </Stack>
              </Stack>
            </Box>

            <Box
              background="neutralSoft"
              borderRadius="small"
              paddingY="medium"
              paddingX="gutter"
              marginTop={{ mobile: 'gutter', tablet: 'none' }}
              style={{
                height: 'min-content',
                minWidth: '300px'
              }}
              flex={1}
            >
              <Stack space="medium">
                <Box display="flex" alignItems="flexStart" width="full">
                  <Box
                    marginRight="xxsmall"
                    style={{ width: '16px', flexShrink: 0 }}
                  >
                    <Image src={phoneIcon.src} alt="" width={16} height={16} />
                  </Box>
                  <Box display="inline" flexDirection="column">
                    <TextLink
                      href="tel:6043288888"
                      size="small"
                      rel="noreferrer noopener"
                      target="_blank"
                    >
                      {t('firstarrealtyinc:phoneBusiness')}
                    </TextLink>{' '}
                    |{' '}
                    <TextLink
                      href="tel:6046218888"
                      size="small"
                      rel="noreferrer noopener"
                      target="_blank"
                    >
                      {t('firstarrealtyinc:phoneAfterHours')}
                    </TextLink>
                  </Box>
                </Box>
                <Box display="flex" alignItems="center" width="full">
                  <Box
                    marginRight="xxsmall"
                    style={{ width: '16px', flexShrink: 0 }}
                  >
                    <Image src={emailIcon.src} alt="" width={16} height={16} />
                  </Box>
                  <TextLink
                    href={`mailto:info@firstarrealty.com`}
                    rel="noreferrer noopener"
                    size="small"
                  >
                    info@firstarrealty.com
                  </TextLink>
                </Box>
                <Box display="flex" alignItems="center" width="full">
                  <Box
                    marginRight="xxsmall"
                    style={{ width: '16px', flexShrink: 0 }}
                  >
                    <Image src={globeIcon.src} alt="" width={16} height={16} />
                  </Box>
                  <TextLink
                    href="https://www.firstarrealty.com/"
                    rel="noreferrer noopener"
                    target="_blank"
                    size="small"
                  >
                    https://www.firstarrealty.com/
                  </TextLink>
                </Box>
              </Stack>
            </Box>
          </Box>
        </Stack>
      </Box>

      <Box textAlign="center" paddingBottom="gutter">
        <Heading level="4">{t('firstarrealtyinc:Our Portfolio')}</Heading>
      </Box>
    </>
  )
}

export default FirstarRealtyIncProfile
